"use client";

import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { LandingSectionBadge } from "./LandingSectionBadge";

const snapshots = [
  {
    title: "Debt Recovery",
    description: "Real-time view of recovery performance, debt summary, and key metrics with interactive analytics",
    images: [
      "/images/debt1.svg",
      "/images/debt2.svg",
      "/images/debt3.svg",
    ],
  },
  {
    title: "Campaign Management",
    description: "Create and manage multi-channel campaigns across SMS, Email, WhatsApp, Voice, and IVR",
    images: [
      "/images/Campaign.svg",
      "/images/Monitoring.svg",
    ],
  },
  {
    title: "Customer Segmentation",
    description: "Import, filter, and segment customers with advanced batch processing and automation",
    images: [
      "/images/All Customers.svg",
      "/images/import.png",
    ],
  },
];

export default function ProductSnapshots() {
  // Generate tab value from title
  const getTabValue = (title: string) => title.toLowerCase().replace(/\s+/g, "-");
  
  const [activeTab, setActiveTab] = useState(getTabValue(snapshots[0].title));
  const [imageIndex, setImageIndex] = useState(0);
  const [imageDirection, setImageDirection] = useState(0);

  // Get current snapshot based on active tab
  const currentIndex = snapshots.findIndex(
    (s) => getTabValue(s.title) === activeTab
  );
  const currentSnapshot = snapshots[currentIndex] || snapshots[0];

  const nextImage = () => {
    if (currentSnapshot.images && currentSnapshot.images.length > 0) {
      setImageDirection(1);
      setImageIndex((prev) => (prev + 1) % currentSnapshot.images.length);
    }
  };

  const prevImage = () => {
    if (currentSnapshot.images && currentSnapshot.images.length > 0) {
      setImageDirection(-1);
      setImageIndex((prev) => (prev - 1 + currentSnapshot.images.length) % currentSnapshot.images.length);
    }
  };

  // Reset image index when tab changes
  useEffect(() => {
    setImageIndex(0);
    setImageDirection(1);
  }, [activeTab]);

  useEffect(() => {
    if (!currentSnapshot.images || currentSnapshot.images.length <= 1) return;
    const n = currentSnapshot.images.length;
    const interval = setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      setImageDirection(1);
      setImageIndex((prev) => (prev + 1) % n);
    }, 7000);
    return () => clearInterval(interval);
  }, [activeTab, currentSnapshot.images]);

  const imageVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
      scale: 0.95,
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 300 : -300,
      opacity: 0,
      scale: 0.95,
    }),
  };

  return (
    <section className="relative overflow-hidden bg-gradient-to-b from-background via-blue-50/35 to-background py-20 dark:via-blue-950/15 lg:py-32">
      {/* Background decorative elements */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        <div className="absolute right-10 top-20 h-96 w-96 rounded-full bg-blue-400/15 blur-2xl" />
        <div className="absolute bottom-20 left-10 h-96 w-96 rounded-full bg-cyan-400/15 blur-2xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          className="text-center mb-16 lg:mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="mb-6 flex justify-center"
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: 0.1 }}
          >
            <LandingSectionBadge className="mb-0">Product showcase</LandingSectionBadge>
          </motion.div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight bg-gradient-to-r from-primary via-blue-600 to-cyan-600 bg-clip-text text-transparent mb-6">
            See GrowFinx in action
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            Explore powerful features designed to streamline your financial operations and drive better results
          </p>
        </motion.div>

        <div className="max-w-6xl mx-auto">
          {/* Tabs Navigation */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex justify-center mb-8">
              <TabsList className="inline-flex h-auto w-fit items-center justify-center gap-1 rounded-2xl border border-border/60 bg-muted/50 p-1.5 shadow-md">
                {snapshots.map((snapshot) => {
                  const tabValue = getTabValue(snapshot.title);
                  return (
                    <TabsTrigger
                      key={tabValue}
                      value={tabValue}
                      className="!h-auto min-w-fit !w-auto !flex-none whitespace-nowrap rounded-xl px-5 py-2.5 text-sm font-semibold !border-0 transition-all duration-300 data-[state=active]:bg-primary data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-primary/25 data-[state=inactive]:text-muted-foreground data-[state=inactive]:hover:bg-muted/80 data-[state=inactive]:hover:text-foreground"
                    >
                      {snapshot.title}
                    </TabsTrigger>
                  );
                })}
              </TabsList>
            </div>

            {/* Tab Content */}
            {snapshots.map((snapshot) => {
              const tabValue = getTabValue(snapshot.title);
              return (
                <TabsContent key={tabValue} value={tabValue} className="mt-0">
                  <div>
                    {/* Section Description */}
                    <div className="text-center mb-6">
                      <p className="text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg">
                        {snapshot.description}
                      </p>
                    </div>

                    {/* Image Display */}
                    <Card className="overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-br from-card/95 via-background/90 to-primary/[0.04] shadow-[0_28px_80px_-32px_oklch(0.55_0.18_245_/_0.35)] dark:from-gray-900/90 dark:via-blue-950/40 dark:to-cyan-950/30">
                      <div className="aspect-video bg-gradient-to-br from-blue-500/5 via-cyan-500/5 to-blue-600/5 relative overflow-hidden">
                        {snapshot.images && snapshot.images.length > 0 ? (
                          <div className="w-full h-full flex flex-col items-center justify-center relative p-4 sm:p-6">
                            <div className="w-full h-full flex items-center justify-center mb-4 relative overflow-hidden">
                              <AnimatePresence initial={false} mode="wait" custom={imageDirection}>
                                <motion.div
                                  key={imageIndex}
                                  custom={imageDirection}
                                  variants={imageVariants}
                                  initial="enter"
                                  animate="center"
                                  exit="exit"
                                  transition={{
                                    x: { type: "tween", duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] },
                                    opacity: { duration: 0.25 },
                                    scale: { duration: 0.25 },
                                  }}
                                  className="absolute inset-0 flex items-center justify-center"
                                >
                                  <img
                                    src={snapshot.images[imageIndex]}
                                    alt={`${snapshot.title} - Image ${imageIndex + 1}`}
                                    className="max-w-full max-h-full object-contain drop-shadow-2xl rounded-lg"
                                  />
                                </motion.div>
                              </AnimatePresence>
                            </div>
                            {snapshot.images.length > 1 && (
                              <div className="flex flex-col items-center gap-3 mt-4">
                                <div className="flex items-center gap-2">
                                  {snapshot.images.map((_, idx) => (
                                    <button
                                      key={idx}
                                      type="button"
                                      onClick={() => {
                                        setImageDirection(idx > imageIndex ? 1 : -1);
                                        setImageIndex(idx);
                                      }}
                                      className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${
                                        idx === imageIndex ? "w-6 bg-primary" : "w-2 bg-primary/35 hover:bg-primary/50"
                                      }`}
                                      aria-label={`Go to image ${idx + 1}`}
                                      aria-current={idx === imageIndex ? "true" : undefined}
                                    />
                                  ))}
                                </div>
                                <div className="flex gap-3">
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-9 w-9 rounded-full border-2 border-primary/30 bg-white/95 hover:border-primary/60 dark:bg-gray-800/95"
                                    onClick={prevImage}
                                    aria-label="Previous image"
                                  >
                                    <ChevronLeft className="h-4 w-4 text-primary" />
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="icon"
                                    className="h-9 w-9 rounded-full border-2 border-primary/30 bg-white/95 hover:border-primary/60 dark:bg-gray-800/95"
                                    onClick={nextImage}
                                    aria-label="Next image"
                                  >
                                    <ChevronRight className="h-4 w-4 text-primary" />
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="p-12 text-center">
                            <div className="mx-auto mb-4 flex h-32 w-32 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 via-cyan-500 to-blue-600 shadow-2xl">
                              <span className="text-4xl">📊</span>
                            </div>
                            <h3 className="mb-2 text-xl font-semibold text-foreground">{snapshot.title}</h3>
                            <p className="mx-auto max-w-md text-muted-foreground">{snapshot.description}</p>
                          </div>
                        )}
                      </div>
                    </Card>
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        </div>
      </div>
    </section>
  );
}

